import React, { useState } from "react";
import {
    Typography,
    Box,
    IconButton,
    Collapse,
    Link as MuiLink,
    ClickAwayListener,
} from "@mui/material";
import { Handbag } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@ecommerce/store";
import { AddToCartCollapse } from "./AddToCartCollapse";
import { Product } from "../../types/Product";
import { numberFormatter } from "@ecommerce/shared-utils";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";
import { OVERLAY_STYLES } from "../../styles/overlayStyles";
import { HttpProduct } from "../../http";
import { useTheme } from "../../util/themes";

interface ProductCardProps {
    product: Product;
    variant?: "carousel" | "showcase-small" | "showcase-large" | "carousel-border";
    showDiscount?: boolean;
    discountText?: string;
    onClick?: (productSku: string) => void;
}

export default function ProductCard({
    product,
    variant = "carousel",
    showDiscount = false,
    discountText = "10% OFF\nCOMPRANDO 1 O MÁS",
    onClick,
}: ProductCardProps) {
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const theme = useTheme();
    const [showAddToCart, setShowAddToCart] = useState(false);
    const [selectedVariantForDialog, setSelectedVariantForDialog] = useState<any>(null);
    const isDefaultVariantOutOfStock =
        (product.stock ?? 0) <= (product.stockMinimoPublicacion ?? 0);

    const href = `/productos/${product.sku}`;

    const handleClick = () => {
        if (onClick) {
            onClick(product.sku);
        } else {
            navigate(`/productos/${product.sku}`);
        }
    };

    const handleLinkClick = (e: React.MouseEvent) => {
        if (!onClick) return;

        const isLeftClick = e.button === 0;
        const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

        if (isLeftClick && !isModified) {
            e.preventDefault();
            onClick(product.sku);
        }
    };

    const handleBagClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (isDefaultVariantOutOfStock) return;

        try {
            console.log("PRODUCT CARD PRODUCT:", product);
            console.log("VARIANTE DEFAULT DEL PRODUCTO:", product.idProductoVarianteDefault);

            const variantDetail = await HttpProduct.getProductVariantDetail(
                product.idProductoVarianteDefault,
            );

            setSelectedVariantForDialog({
                id: variantDetail.productoVariante.id,
                price: variantDetail.productoVariante.precio,
                stock: variantDetail.productoVariante.stock,
                attributes: variantDetail.productoVarianteAtributoValores.map((v) => ({
                    name: v.nombreAtributo,
                    value: v.nombreAtributoValor,
                })),
            });

            setShowAddToCart((prev) => !prev);
        } catch (error) {
            console.error("Error loading default variant:", error);
        }
    };

    const handleCloseAddToCart = () => {
        setShowAddToCart(false);
    };

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        if ("button" in event && event.button !== 0) return;

        handleCloseAddToCart();
    };

    const handleAddToCart = async (e: React.MouseEvent) => {
        e.stopPropagation();

        try {
            const variantDetail = await HttpProduct.getProductVariantDetail(
                product.idProductoVarianteDefault,
            );

            const variant = variantDetail.productoVariante;

            const attributes = variantDetail.productoVarianteAtributoValores.map((v) => ({
                name: v.nombreAtributo,
                value: v.nombreAtributoValor,
            }));

            const cartItem = {
                idProduct: product.id.toString(),
                idProductVariant: variant.id,
                name: product.nombre,
                price: variant.precio,
                image: product.urlImagenPrincipal,
                stock: variant.stock,
                quantity: 1,
                attributes,
            };

            // 3?? Lo agrego al carrito
            await addToCart(cartItem);
            setShowAddToCart(false);
        } catch (error) {
            console.error("Error adding variant to cart:", error);
        }
    };

    const handleViewDetails = (e: React.MouseEvent) => {
        e.stopPropagation();
        handleClick();
    };

    const renderCardContent = () => (
        <>
            {/* Discount Banner */}
            {showDiscount && (
                <Box sx={OVERLAY_STYLES.discount}>
                    {discountText.split("\n").map((line, index) => (
                        <Typography sx={TYPOGRAPHY_STYLES.rubikMedium}>{line}</Typography>
                    ))}
                </Box>
            )}

            {/* Product Image Container */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    flexGrow: 1,
                }}
            >
                <img
                    src={product.urlImagenPrincipal}
                    alt={product.nombre}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                    }}
                />

                <IconButton
                    sx={{
                        position: "absolute",
                        left: "50%",
                        bottom: isDefaultVariantOutOfStock ? "8px" : showAddToCart ? "95px" : "8px",
                        transform: "translateX(-50%)",
                        backgroundColor: isDefaultVariantOutOfStock ? "grey.300" : "white",
                        color: isDefaultVariantOutOfStock ? "grey.600" : "black",
                        borderRadius: 0,
                        boxShadow: 1,
                        "&:hover": {
                            backgroundColor: isDefaultVariantOutOfStock ? "grey.300" : "grey.200",
                        },
                        zIndex: 4,
                        transition: "bottom 0.3s ease",
                    }}
                    disabled={isDefaultVariantOutOfStock}
                    onClick={(e) => {
                        if (isDefaultVariantOutOfStock) return;
                        handleBagClick(e);
                    }}
                >
                    <Handbag size={20} />
                </IconButton>

                {isDefaultVariantOutOfStock && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: "8px",
                            left: 0,
                            right: 0,
                            textAlign: "center",
                            zIndex: 5,
                            p: 1.2,
                            background: "rgba(255,255,255,0.85)",
                            backdropFilter: "blur(2px)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "11px",
                                lineHeight: "14px",
                                fontFamily: "'Rubik', sans-serif",
                                color: "text.secondary",
                                mb: 0.5,
                                px: 1.4,
                            }}
                        >
                            Esta variante está agotada.
                            <br />
                            Podés elegir otra disponible.
                        </Typography>

                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Box
                                sx={{
                                    fontSize: "11px",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    color: "primary.main",
                                    "&:hover": { color: "primary.dark" },
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/productos/${product.sku}`);
                                }}
                            >
                                Ver variantes disponibles
                            </Box>
                        </Box>
                    </Box>
                )}

                <Collapse
                    in={showAddToCart}
                    timeout={300}
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 3,
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            height: "95px",
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                    >
                        <AddToCartCollapse
                            product={product}
                            variant={selectedVariantForDialog}
                            onViewDetails={handleViewDetails}
                        />
                    </Box>
                </Collapse>
            </Box>

            {/* Product Info */}
            <Box
                sx={{
                    height: "110px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    paddingBottom: 3,
                    textAlign: "center",
                    backgroundColor: theme.colors.background.paper,
                    flexShrink: 0,
                }}
            >
                <Typography
                    sx={{
                        ...TYPOGRAPHY_STYLES.rubikLarge,
                        fontWeight: 300,
                        color: theme.colors.text.primary,
                        lineHeight: "22px",
                        mb: 1,
                        textTransform: "uppercase",
                    }}
                >
                    {product.nombre}
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontFamily: "'Rubik', sans-serif",
                        fontSize: "16px",
                        color: theme.colors.text.primary,
                    }}
                >
                    {numberFormatter.toStringWithAmount(product.precio, "$")}
                </Typography>
            </Box>
        </>
    );

    const renderCarouselCard = () => (
        <MuiLink
            href={href}
            underline="none"
            color="inherit"
            sx={{
                position: "relative",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                bgcolor: theme.colors.background.paper,
                overflow: "hidden", // IMPORTANTE
                width: "100%", // NECESARIO
                textDecoration: "none",
            }}
            onClick={handleLinkClick}
        >
            <Box
                sx={{
                    position: "relative",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    bgcolor: theme.colors.background.paper,
                    overflow: "hidden",
                }}
            >
                {renderCardContent()}
            </Box>
        </MuiLink>
    );

    const renderShowcaseCard = (isLarge: boolean) => (
        <MuiLink
            href={href}
            underline="none"
            color="inherit"
            sx={{
                height: "100%",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                border: "0.1px solid #e0e0e0",
                bgcolor: "white",
                cursor: "pointer",
                width: "100%",
                textDecoration: "none",
            }}
            onClick={handleLinkClick}
        >
            <Box
                sx={{
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    border: "0.1px solid #e0e0e0",
                    bgcolor: "white",
                    cursor: "pointer",
                }}
            >
                {renderCardContent()}
            </Box>
        </MuiLink>
    );

    const renderCarouselBorderCard = () => (
        <MuiLink
            href={href}
            underline="none"
            color="inherit"
            sx={{
                position: "relative",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                bgcolor: theme.colors.background.paper,
                borderLeft: "0.1px solid",
                borderRight: "0.1px solid",
                borderTop: "0.1px solid",
                borderColor: theme.colors.greyLight,
                overflow: "hidden",
                width: "100%",
                textDecoration: "none",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    bgcolor: theme.colors.background.paper,
                    borderLeft: "0.1px solid",
                    borderRight: "0.1px solid",
                    borderTop: "0.1px solid",
                    borderColor: theme.colors.greyLight,
                    overflow: "hidden",
                }}
            >
                {renderCardContent()}
            </Box>
        </MuiLink>
    );

    const renderCard = () => {
        switch (variant) {
            case "carousel":
                return renderCarouselCard();
            case "carousel-border":
                return renderCarouselBorderCard();
            case "showcase-small":
                return renderShowcaseCard(false);
            case "showcase-large":
                return renderShowcaseCard(true);
            default:
                return renderCarouselCard();
        }
    };

    return <ClickAwayListener onClickAway={handleClickAway}>{renderCard()}</ClickAwayListener>;
}
